import React, { useState, useEffect, useCallback } from "react";
import { Users, FileText, UserPlus, TrendingUp, RefreshCw, Loader2, Mail, Phone, KeyRound } from "lucide-react";
import { getStats } from "../lib/api";

const LANG_NAMES = {
  gu: "Gujarati", en: "English", ar: "Arabic", hi: "Hindi", ur: "Urdu", bn: "Bengali",
  mr: "Marathi", ta: "Tamil", te: "Telugu", kn: "Kannada", ml: "Malayalam", pa: "Punjabi",
  es: "Spanish", fr: "French", de: "German", pt: "Portuguese", it: "Italian", ru: "Russian",
  zh: "Chinese", ja: "Japanese", ko: "Korean", tr: "Turkish", fa: "Persian", sw: "Swahili", id: "Indonesian",
};

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }); }
  catch { return "—"; }
}
function ago(iso) {
  if (!iso) return "never";
  try {
    const d = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
    if (d <= 0) return "today";
    if (d === 1) return "yesterday";
    if (d < 30) return `${d}d ago`;
    return fmtDate(iso);
  } catch { return "—"; }
}

function StatCard({ icon: Icon, label, value, color }) {
  return (
    <div className="yb-tactile bg-white p-4 flex items-center gap-3" style={{ boxShadow: "0 4px 0 0 #1A1C19" }}>
      <div className="w-12 h-12 rounded-xl flex items-center justify-center border-2 border-[#1A1C19] shrink-0" style={{ background: color }}>
        <Icon className="w-6 h-6" />
      </div>
      <div>
        <p className="yb-heading text-3xl font-extrabold leading-none">{value}</p>
        <p className="text-sm text-[#50564F]">{label}</p>
      </div>
    </div>
  );
}

export default function StatsPanel({ session }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try { setStats(await getStats(session.user_id)); } catch (e) { /* ignore */ } finally { setLoading(false); }
  }, [session.user_id]);

  useEffect(() => { refresh(); }, []); // eslint-disable-line

  if (loading && !stats) {
    return <div className="max-w-4xl mx-auto px-4 py-12 flex justify-center"><Loader2 className="w-10 h-10 animate-spin" /></div>;
  }
  if (!stats) return null;

  return (
    <div className="max-w-4xl mx-auto px-4 py-6 space-y-6" data-testid="stats-panel">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-[#1A1C19] text-[#FFD600] flex items-center justify-center border-3 border-[#1A1C19]">
            <TrendingUp className="w-7 h-7" />
          </div>
          <div>
            <h2 className="yb-heading text-2xl font-extrabold">Usage & Members</h2>
            <p className="text-sm text-[#50564F]">Who used what, and who joined</p>
          </div>
        </div>
        <button onClick={refresh} data-testid="stats-refresh" className="yb-tactile bg-white p-3"><RefreshCw className="w-5 h-5" /></button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard icon={Users} label="Total logins" value={stats.total_users} color="#FFD600" />
        <StatCard icon={UserPlus} label="Members joined" value={stats.total_members} color="#DCFCE7" />
        <StatCard icon={FileText} label="Documents saved" value={stats.total_documents} color="#DBEAFE" />
        <StatCard icon={TrendingUp} label="New in 30 days" value={stats.new_members_30d} color="#FEF3C7" />
      </div>

      <div className="yb-tactile bg-[#FEFCE8] p-3 text-sm font-bold text-[#50564F]">
        {stats.new_members_7d} new member{stats.new_members_7d === 1 ? "" : "s"} in the last 7 days ·
        {" "}{stats.new_members_30d} in the last 30 days
      </div>

      <div className="space-y-2">
        <h3 className="yb-heading font-extrabold text-lg">Per-person usage</h3>
        {stats.per_user.map((u) => (
          <div key={u.id} className="yb-tactile bg-white p-4" data-testid={`stats-row-${u.id}`}>
            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex-1 min-w-0">
                <p className="font-bold truncate">
                  {u.name}
                  {u.is_default && <span className="text-xs text-[#50564F]"> (default)</span>}
                  {u.role === "master" && <span className="text-xs ml-1 px-2 py-0.5 rounded-full bg-[#1A1C19] text-[#FFD600]">master</span>}
                </p>
                <p className="text-sm text-[#50564F] flex items-center gap-3 flex-wrap">
                  <span className="flex items-center gap-1"><KeyRound className="w-4 h-4" /> {u.pin || "—"}</span>
                  <span>{LANG_NAMES[u.language] || u.language}</span>
                  {u.email && <span className="flex items-center gap-1"><Mail className="w-4 h-4" /> {u.email}</span>}
                  {u.phone && <span className="flex items-center gap-1"><Phone className="w-4 h-4" /> {u.phone}</span>}
                </p>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3">
              <div className="bg-[#DBEAFE] rounded-xl border-2 border-[#1A1C19] px-3 py-2 text-center">
                <p className="text-xl font-extrabold">{u.doc_count}</p><p className="text-xs text-[#50564F]">docs</p>
              </div>
              <div className="bg-[#DCFCE7] rounded-xl border-2 border-[#1A1C19] px-3 py-2 text-center">
                <p className="text-xl font-extrabold">{u.login_count}</p><p className="text-xs text-[#50564F]">logins</p>
              </div>
              <div className="bg-white rounded-xl border-2 border-[#1A1C19] px-3 py-2 text-center">
                <p className="text-sm font-extrabold">{ago(u.last_active)}</p><p className="text-xs text-[#50564F]">last active</p>
              </div>
              <div className="bg-white rounded-xl border-2 border-[#1A1C19] px-3 py-2 text-center">
                <p className="text-sm font-extrabold">{fmtDate(u.joined)}</p><p className="text-xs text-[#50564F]">joined</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      <p className="text-xs text-[#50564F] italic">Note: "usage" is measured by logins, documents saved, and last activity. Exact time-spent per session is not tracked.</p>
    </div>
  );
}
